import { computed, ref, watch, type Ref } from "vue";

import { Calendar } from "@ctrl/controller-rpc";

import { isDraggingTask } from "@/components/agenda/dnd";
import {
  asTaskDefaultCalendarId,
  useTaskDefaultCalendar,
  type CalendarsRef,
} from "@/composables/useCalendars";
import type { UseProfile } from "@/composables/useProfile";

/**
 * Owns the keyboard listener and the task-default-calendar state, freed by
 * the returned `release()`.
 */
export async function useCalendarSwitcher(
  calendars: CalendarsRef,
  profile: Ref<UseProfile>
) {
  const taskDefaultCalendar = useTaskDefaultCalendar(profile);
  let instance: ((e: KeyboardEvent) => void) | null = null;

  // Keyboard handler for cycling through calendars during drag with TAB
  const handleKeyDown = (e: KeyboardEvent) => {
    if (writableCalendars.value.length <= 1) return;

    const currentIndex = writableCalendars.value.findIndex(
      (cal) => cal.id === selectedCalendarId.value
    );
    const validIndex = currentIndex >= 0 ? currentIndex : 0;

    if (e.key === "Tab" || e.code === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (e.shiftKey) {
        // Cycle backward
        const newIndex =
          (validIndex - 1 + writableCalendars.value.length) %
          writableCalendars.value.length;
        selectedCalendarId.value = writableCalendars.value[newIndex].id;
      } else {
        // Cycle forward
        const newIndex = (validIndex + 1) % writableCalendars.value.length;
        selectedCalendarId.value = writableCalendars.value[newIndex].id;
      }
      return false;
    }
  };

  let unregistered = false;
  const unregisterKeyboardListener = () => {
    if (unregistered) return;
    unregistered = true;
    // Only remove if we're still the instance's active handler
    if (instance) {
      document.removeEventListener("keydown", instance, true);
      instance = null;
    }
  };

  const registerKeyboardListener = (isTaskDragging?: { value: boolean }) => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Only enable when dragging a task (disable for allocation duplication)
      const allowed =
        typeof isTaskDragging?.value === "boolean"
          ? isTaskDragging.value
          : isDraggingTask();
      if (!allowed) return;
      handleKeyDown(e);
    };

    // Remove previous handler for THIS composable instance (if any), then attach
    if (instance) {
      document.removeEventListener("keydown", instance, true);
    }
    document.addEventListener("keydown", onKeyDown, {
      capture: true,
      passive: false,
    });
    instance = onKeyDown;
    // Note: cleanup happens through the returned release()
  };

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    unregisterKeyboardListener();
    stopDefaultWatch();
    taskDefaultCalendar.release();
  };

  const calendarsMap = computed(() => {
    const map: Record<string, Calendar> = {};
    const writable = (calendars.value?.all ?? []).filter(
      (c) => c.isWritable && !c.disabled
    );
    writable.forEach((cal) => {
      map[cal.id] = cal;
    });
    return map;
  });

  const writableCalendars = computed(() => Object.values(calendarsMap.value));

  const defaultCalendar = computed(() => calendars.value?.default ?? null);
  // The calendar a dropped task lands in before the user Tab-picks one: the
  // task default calendar (Calendars settings) while it resolves to a
  // writable enabled calendar, the events default otherwise.
  const preferredCalendar = computed(() => {
    const stored = asTaskDefaultCalendarId(taskDefaultCalendar.value);
    const calendar = stored ? calendars.value?.byId.get(stored) : undefined;
    return calendar && calendar.isWritable && !calendar.disabled
      ? calendar
      : defaultCalendar.value;
  });
  // Follow the preferred calendar until the user picks one (the store may
  // still be loading when the composable is created).
  const selectedCalendarId = ref(preferredCalendar.value?.id);
  const stopDefaultWatch = watch(preferredCalendar, (def, oldDef) => {
    if (
      def &&
      (selectedCalendarId.value === undefined ||
        selectedCalendarId.value === oldDef?.id)
    )
      selectedCalendarId.value = def.id;
  });

  return {
    defaultCalendar,
    selectedCalendarId,
    calendarsMap,
    registerKeyboardListener,
    release,
    unregisterKeyboardListener,
  };
}
